import axios from 'axios';
import { SupportUserMapper } from './SupportUser/SupportUserMapper.js';
import { parseRecordToSnakeCase, cleanFalsyAPIValues } from '../../utils/api.js';
import { PickupSupportTicketMapper } from './SupportTicket/PickupSupportTicket/PickupSupportTicketMapper.js';
import { LossSupportTicketMapper } from './SupportTicket/LossSupportTicket/LossSupportTicketMapper.js';
import { DamageSupportTicketMapper } from './SupportTicket/DamageSupportTicket/DamageSupportTicketMapper.js';
import { LabelSupportTicketMapper } from './SupportTicket/LabelSupportTicket/LabelSupportTicketMapper.js';
import { DeliverySupportTicketMapper } from './SupportTicket/DeliverySupportTicket/DeliverySupportTicketMapper.js';
import { BillingSupportTicketMapper } from './SupportTicket/BillingSupportTicket/BillingSupportTicketMapper.js';
import { OtherSupportTicketMapper } from './SupportTicket/OtherSupportTicket/OtherSupportTicketMapper.js';
import { SupportTicketMessageMapper } from './SupportTicketMessage/SupportTicketMessageMapper.js';
import { SupportTicketMapper } from './SupportTicket/SupportTicketMapper.js';
import { SupportTicketType } from './SupportTicket/SupportTicket.js';
import { CancelSupportTicketMapper } from './SupportTicket/CancelSupportTicket/CancelSupportTicketMapper.js';

class SupportCenterRepository {
  constructor(apiClient) {
    this.apiClient = apiClient;
  }

  /**
   * Creates a pickup support ticket for a particular shipment
   *
   * @param payload Pickup support ticket payload
   * @returns Pickup support ticket information
   */
  createPickupSupportTicket(payload) {
    return this.createSupportTicket(payload, PickupSupportTicketMapper);
  }

  /**
   * Creates a loss support ticket for a particular shipment
   *
   * @param payload Loss support ticket payload
   * @returns Loss support ticket information
   */
  createLossSupportTicket(payload) {
    return this.createSupportTicket(payload, LossSupportTicketMapper);
  }

  /**
   * Creates a damage support ticket for a particular shipment
   *
   * @param payload Damage support ticket payload
   * @returns Damage support ticket information
   */
  createDamageSupportTicket(payload) {
    return this.createSupportTicket(payload, DamageSupportTicketMapper);
  }

  /**
   * Creates a label support ticket for a particular shipment
   *
   * @params payload Label support ticket payload
   * @returns Label support ticket information
   */
  createLabelSupportTicket(payload) {
    return this.createSupportTicket(payload, LabelSupportTicketMapper);
  }

  /**
   * Creates a delivery support ticket for a particular shipment
   *
   * @params payload Delivery support ticket payload
   * @returns Delivery support ticket information
   */
  createDeliverySupportTicket(payload) {
    return this.createSupportTicket(payload, DeliverySupportTicketMapper);
  }

  /**
   * Creates a billing support ticket for a particular shipment
   *
   * @params payload Billing support ticket payload
   * @returns Billing support ticket information
   */
  createBillingSupportTicket(payload) {
    return this.createSupportTicket(payload, BillingSupportTicketMapper);
  }

  /**
   * Creates a other support ticket for a particular shipment
   *
   * @params payload Other support ticket payload
   * @returns Other support ticket information
   */
  createOtherSupportTicket(payload) {
    return this.createSupportTicket(payload, OtherSupportTicketMapper);
  }
  createSupportTicket(payload, mapper) {
    return this.apiClient.post(`v1/support/requests`, mapper.createApiPayload(payload)).then(_ref => {
      let {
        data
      } = _ref;
      return mapper.createModelFromResponse(data);
    });
  }

  /**
   * Allows to search and retrieve all the requests opened by a client.
   *
   * @params Filter params
   * @returns Support ticket list
   */
  get(id) {
    return this.apiClient.get(`v1/support/requests/${id}`)
    // Currently we don't need to map to every support ticket type (list doesn't need more info than the common)
    // If this changes, we should create a function that selects the specific transformer
    .then(_ref2 => {
      let {
        data: supportTicket
      } = _ref2;
      return this.mapSupportTicketMapper(supportTicket);
    });
  }

  /**
   * Allows to search and retrieve all the requests opened by a client.
   *
   * @params Filter params
   * @returns Support ticket list
   */
  list() {
    let filters = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const parsedFilters = parseRecordToSnakeCase(filters);
    return this.apiClient.get('v1/support/requests', cleanFalsyAPIValues(parsedFilters))
    // Currently we don't need to map to every support ticket type (list doesn't need more info than the common)
    // If this changes, we should create a function that selects the specific transformer
    .then(_ref3 => {
      let {
        data: response
      } = _ref3;
      return response.data.map(supportTicket => this.mapSupportTicketMapper(supportTicket));
    });
  }

  /**
   * Allows to retrieve all support ticket messages.
   *
   * @returns Support ticket list
   */
  getMessages(supportTicketId) {
    return this.apiClient.get(`v1/support/requests/${supportTicketId}/messages`).then(_ref4 => {
      let {
        data: response
      } = _ref4;
      return response.data.map(SupportTicketMessageMapper.createModelFromResponse);
    });
  }

  /**
   * Allows to retrieve all support ticket messages.
   *
   * @returns Support ticket list
   */
  sendMessage(supportTicketId, messagePayload) {
    return this.apiClient.post(`v1/support/requests/${supportTicketId}/messages`, SupportTicketMessageMapper.createApiPayload(messagePayload)).then(_ref5 => {
      let {
        data
      } = _ref5;
      return SupportTicketMessageMapper.createModelFromResponse(data);
    });
  }
  mapSupportTicketMapper(data) {
    const {
      type
    } = SupportTicketMapper.createModelFromResponse(data);
    switch (type) {
      case SupportTicketType.PICKUP:
        return PickupSupportTicketMapper.createModelFromResponse(data);
      case SupportTicketType.LOSS:
        return LossSupportTicketMapper.createModelFromResponse(data);
      case SupportTicketType.DAMAGE:
        return DamageSupportTicketMapper.createModelFromResponse(data);
      case SupportTicketType.CANCEL:
        return CancelSupportTicketMapper.createModelFromResponse(data);
      case SupportTicketType.LABEL:
        return LabelSupportTicketMapper.createModelFromResponse(data);
      case SupportTicketType.DELIVERY:
        return DeliverySupportTicketMapper.createModelFromResponse(data);
      case SupportTicketType.BILLING:
        return BillingSupportTicketMapper.createModelFromResponse(data);
      case SupportTicketType.OTHER:
        return OtherSupportTicketMapper.createModelFromResponse(data);
      default:
        return SupportTicketMapper.createModelFromResponse(data);
    }
  }
  uploadAttachment(file) {
    return this.apiClient.get('v1/support/attachments/get_url').then(_ref6 => {
      let {
        data
      } = _ref6;
      return (
        /**
         *  HTTP calls to Google bucket should be send without ApiClient headers because:
         *     1.  It is a security risk to send our authorization keys to 3rd party APIs
         *     2.  Google API reject calls with unexpected HTTP headers
         **/
        axios({
          url: data.upload_url,
          method: 'put',
          data: file
        }).then(() => data.download_url)
      );
    }).then(downloadUrl => this.apiClient.post('v1/support/attachments/upload', {
      content_url: downloadUrl,
      file_name: file.name
    })).then(_ref7 => {
      let {
        data
      } = _ref7;
      return data.attachment_upload_id;
    });
  }
  getCurrentUser() {
    return this.apiClient.get('v1/support/users/self').then(_ref8 => {
      let {
        data
      } = _ref8;
      return SupportUserMapper.createModelFromResponse(data);
    });
  }
}

export { SupportCenterRepository };
//# sourceMappingURL=SupportCenterRepository.js.map
